(function () {
  'use strict';

  if (window.__reefPromoBarLoaded) return;
  window.__reefPromoBarLoaded = true;

  var SELECTOR = '[data-reef-pb]';
  var lastQty = 0;

  // Where the first and last chip sit, as a percentage of the lane. One pair
  // for both variants, so the drawer and the header lay out identically.
  //
  // TICK_END stops short of 100 on purpose: the final percentage marker is
  // right-aligned to the lane edge and needs clear air to the right of the
  // last chip, or it renders underneath it.
  var TICK_START = 5;
  var TICK_END = 86;

  // ---- Price safety check -------------------------------------------------
  // The tier percentages here are a COPY of the rules in the discount app.
  // Nothing enforces that they agree, and when they drift the failure is
  // silent and customer-facing: the product page advertises a price the
  // checkout will not honour.
  //
  // So once there is a real basket, check the assumption against what Shopify
  // actually charged. On a mismatch, stop rewriting prices and let full price
  // show. A customer seeing full price and a better one at checkout is a good
  // surprise; the reverse is a complaint.
  //
  // Tri-state on purpose: 'unknown' means there is nothing to check against -
  // an empty basket discounts nothing - and that must not read as a failure,
  // or prices would never show to a first-time visitor.
  var PRICE_CHECK_TOLERANCE = 1.5; // percentage points, absorbs penny rounding
  var priceCheck = 'unknown'; // 'unknown' | 'ok' | 'mismatch'
  var priceCheckWarned = false;

  // Styles injected from here rather than the theme's Custom CSS field, which
  // is capped at 1500 characters and is already close to full.
  //
  // PRICE ROW: margins are deliberately very tight. On a collection card the
  // row reads "was / now / -50% / i", so loose gaps push the badge into the
  // neighbouring card.
  var STYLES =
    '.reef-price-was{opacity:.45;font-weight:400;margin-right:.3rem}' +
    '.reef-price-now{font-weight:600}' +
    '.reef-price-off{display:inline-block;margin-left:.25rem;padding:.2rem .6rem;border-radius:10rem;' +
    'background:#e0342c;color:#fff;font-size:1.1rem;font-weight:700;line-height:1.5;' +
    'vertical-align:middle;white-space:nowrap}' +
    '.reef-price-info{position:relative;z-index:3;display:inline-grid;place-items:center;' +
    'width:1.7rem;height:1.7rem;margin-left:.2rem;padding:0;' +
    'border:.1rem solid rgba(var(--color-foreground),.3);border-radius:50%;background:transparent;' +
    'color:rgba(var(--color-foreground),.7);font-family:Georgia,serif;font-style:italic;' +
    'font-size:1.15rem;line-height:1;cursor:pointer;vertical-align:middle}' +
    '.reef-price-info:hover{border-color:rgba(var(--color-foreground),.6);' +
    'color:rgb(var(--color-foreground))}' +
    '.reef-price-note{display:block;font-size:1.1rem;opacity:.7;font-weight:400}';

  function injectStyles() {
    if (document.getElementById('reef-price-styles')) return;
    var tag = document.createElement('style');
    tag.id = 'reef-price-styles';
    tag.textContent = STYLES;
    document.head.appendChild(tag);
  }

  // The offers modal is authored inside the promo bar section, and that
  // section's wrapper is position:sticky with a z-index - which makes it a
  // stacking context. Trapped inside it, the modal cannot paint above the
  // header (z-index 5) however high its own z-index goes, which is why the
  // close button kept appearing to sit underneath the header.
  //
  // Lifting the wrapper instead did work, but dragged the BAR up with it - so
  // the full-width header bar then painted over the cart drawer and looked
  // like the drawer's own ladder had grown.
  //
  // Moving the modal to the end of <body> removes it from that context
  // altogether. It becomes a sibling of the cart drawer, later in the
  // document, so it stacks above the drawer and above the header without
  // changing the z-index of either. Idempotent, so re-running is harmless.
  function detachModal() {
    var modal = document.getElementById('ReefPromoModal');
    if (modal && modal.parentNode !== document.body) {
      document.body.appendChild(modal);
    }
  }

  function fill(template, values) {
    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function readConfig(root) {
    var holder = (root || document).querySelector('[data-reef-pb-config]');
    if (!holder) holder = document.querySelector('[data-reef-pb-config]');
    if (!holder) return null;
    try {
      return JSON.parse(holder.textContent);
    } catch (error) {
      return null;
    }
  }

  function label(tier) {
    if (!tier) return '';
    return tier.long || tier.short || '';
  }

  function iconFor(tier) {
    if (tier && tier.icon && tier.icon !== 'none') return tier.icon;
    return 'bottle';
  }

  function word(config, n) {
    return n === 1 ? config.noun_one : config.noun_many;
  }

  function eligibleQty(cart) {
    var config = readConfig(null);
    var exclude = (config && config.exclude) || [];
    var total = 0;

    (cart.items || []).forEach(function (line) {
      if (exclude.indexOf(line.handle) === -1) total += line.quantity;
    });

    return total;
  }

  function compute(config, qty) {
    var tiers = config.tiers || [];
    var count = tiers.length;
    var reachedIndex = 0;
    var i;

    for (i = 0; i < count; i++) {
      if (qty >= tiers[i].qty) reachedIndex = i + 1;
    }

    // Milestones flagged target:false are steps that do not change the offer,
    // so the progress message skips past them to the next real upgrade.
    var next = null;
    for (i = reachedIndex; i < count; i++) {
      if (tiers[i].target !== false) {
        next = tiers[i];
        break;
      }
    }

    return {
      current: reachedIndex > 0 ? tiers[reachedIndex - 1] : null,
      next: next,
      remaining: next ? next.qty - qty : 0,
      reachedIndex: reachedIndex
    };
  }

  // Compare the discount actually applied to the basket against the rate this
  // code is about to advertise. Only eligible lines are measured, because the
  // excluded handles are never discounted and would drag the average down.
  function auditCart(cart, qty) {
    var config = readConfig(null);
    if (!config || !config.live_pricing) return;

    var exclude = config.exclude || [];
    var gross = 0;
    var net = 0;

    (cart.items || []).forEach(function (line) {
      if (exclude.indexOf(line.handle) !== -1) return;
      gross += line.original_line_price;
      net += line.final_line_price;
    });

    // Nothing eligible in the basket - no evidence, so make no claim.
    if (gross <= 0) {
      priceCheck = 'unknown';
      return;
    }

    var actual = ((gross - net) / gross) * 100;
    var state = compute(config, qty);
    var expected = (state.current && state.current.discount) || 0;

    if (Math.abs(actual - expected) <= PRICE_CHECK_TOLERANCE) {
      priceCheck = 'ok';
      return;
    }

    priceCheck = 'mismatch';

    if (!priceCheckWarned && window.console && console.warn) {
      priceCheckWarned = true;
      console.warn(
        '[REEF promo bar] Tier pricing disabled: the basket was discounted ' +
          actual.toFixed(2) +
          '% but the bar expected ' +
          expected +
          '% at ' +
          qty +
          ' items. Showing full price until the theme tier settings and the ' +
          'discount app agree.'
      );
    }
  }

  // The section positions each chip in proportion to its QUANTITY, which
  // bunches the low milestones together (1, 2, 3) and strands the high ones
  // (5, 8) out to the right. Respace them evenly by index instead, so the
  // steps read as equal stages rather than a number line.
  //
  // This deliberately rewrites the inline `left` the section set, because
  // fillPercent() below reads those same inline offsets - so the progress
  // fill re-interpolates against the new spacing and stays in step.
  //
  // Returns the offsets used, because spaceBands() has to place the markers
  // against these and not against the section's stale quantity-based ones.
  function spaceTicks(root) {
    var ticks = root.querySelectorAll('[data-reef-pb-tick]');
    var count = ticks.length;
    var i;

    if (!count) return [];

    if (count === 1) {
      ticks[0].style.left = '50%';
      return [50];
    }

    var lefts = [];

    for (i = 0; i < count; i++) {
      var left = TICK_START + ((TICK_END - TICK_START) * i) / (count - 1);
      ticks[i].style.left = left.toFixed(2) + '%';
      lefts.push(left);
    }

    return lefts;
  }

  // The percentage markers must sit midway between the chips they separate.
  //
  // The section works those offsets out from quantity, but spaceTicks() has
  // just replaced that spacing with even steps - so the Liquid positions are
  // stale by the time they reach the page, which is what put the markers on
  // top of the chips rather than between them. Recompute from the offsets
  // spaceTicks() actually used, so the two can never drift apart again.
  //
  // A marker on the LAST milestone has no following chip, so it is pinned to
  // the right-hand end of the lane. The CSS gives that one an --end modifier
  // which right-aligns it rather than centring it.
  function spaceBands(root, lefts) {
    var bands = root.querySelectorAll('[data-reef-pb-band]');
    if (!bands.length || !lefts.length) return;

    Array.prototype.forEach.call(bands, function (band) {
      var index = parseInt(band.getAttribute('data-reef-pb-band'), 10);
      if (isNaN(index) || index < 1) return;

      var a = lefts[index - 1];
      var b = lefts[index];

      if (typeof a !== 'number') return;

      if (typeof b !== 'number') {
        band.style.left = '100%';
        band.classList.add('reef-pb__band--end');
        return;
      }

      band.style.left = ((a + b) / 2).toFixed(2) + '%';
      band.classList.remove('reef-pb__band--end');
    });
  }

  // How far the black line runs. Two behaviours, chosen by the size of the
  // gap to the next milestone.
  //
  // CONSECUTIVE milestones (1 -> 2 -> 3): there is no quantity in between, so
  // interpolating would leave the line parked on the chip just earned and it
  // would never appear to move. The line runs to the NEXT chip instead, while
  // that chip stays grey until it is actually earned.
  //
  // WIDER gaps (3 -> 5, 5 -> 8): there are quantities in between, so the line
  // fills proportionally and each added item visibly advances it.
  function fillPercent(root, config, qty) {
    var tiers = config.tiers || [];
    var ticks = root.querySelectorAll('[data-reef-pb-tick]');
    var points = [];
    var i;

    for (i = 0; i < ticks.length; i++) {
      var left = parseFloat(ticks[i].style.left);
      if (isNaN(left) || !tiers[i]) continue;
      points.push({ qty: tiers[i].qty, left: left });
    }

    if (!points.length || qty <= 0) return 0;

    var first = points[0];
    var last = points[points.length - 1];

    if (qty >= last.qty) return 100;
    if (qty < first.qty) return (qty / first.qty) * first.left;

    var index = 0;
    for (i = 0; i < points.length; i++) {
      if (qty >= points[i].qty) index = i;
    }

    var a = points[index];
    var b = points[index + 1];
    if (!b) return 100;

    var span = b.qty - a.qty;
    if (span <= 1) return b.left;

    return a.left + ((qty - a.qty) / span) * (b.left - a.left);
  }

  function pill(config, qty, state) {
    if (!state.next) {
      return {
        icon: iconFor(state.current),
        text: fill(config.text_max, { reward: label(state.current) })
      };
    }

    return {
      icon: iconFor(state.next),
      text: fill(config.text_next, {
        n: state.remaining,
        item: word(config, state.remaining),
        reward: label(state.next)
      })
    };
  }

  function applyPricing(qty) {
    var config = readConfig(null);
    if (!config) return;

    var nodes = document.querySelectorAll('[data-reef-price]');
    if (!nodes.length) return;

    // An empty cart still earns the first tier. Adding one item qualifies for
    // 20%, so showing full price to a browsing customer understates what they
    // would actually pay. Price as though this item is already in the basket.
    var state = compute(config, qty > 0 ? qty : 1);
    var percent = 0;
    if (config.live_pricing && state.current && state.current.discount) {
      percent = state.current.discount;
    }

    // The basket disagreed with the tier table, so make no price claim at all.
    if (priceCheck === 'mismatch') percent = 0;

    var exclude = config.exclude || [];
    var note = config.sale_note || '';
    var badge = '-' + Math.round(percent) + '%';

    Array.prototype.forEach.call(nodes, function (el) {
      var slot = el.querySelector('.price__regular .price-item--regular');
      if (!slot) return;

      if (!el.hasAttribute('data-reef-price-original')) {
        el.setAttribute('data-reef-price-original', slot.innerHTML);
      }

      var original = el.getAttribute('data-reef-price-original');
      var base = parseInt(el.getAttribute('data-reef-price'), 10);
      var handle = el.getAttribute('data-reef-price-handle');

      if (!percent || !base || exclude.indexOf(handle) !== -1) {
        if (el.hasAttribute('data-reef-priced')) {
          slot.innerHTML = original;
          el.removeAttribute('data-reef-priced');
        }
        return;
      }

      var wasText = (base / 100).toFixed(2);
      if (original.indexOf(wasText) === -1) return;

      // ROUND UP, not down. Shopify floors the DISCOUNT, which is the same
      // thing as ceiling the final price - confirmed against six figures
      // across three prices. Math.floor here made every price a penny cheaper
      // than the basket, which is the one direction that matters.
      var nowCents = Math.ceil(base * (1 - percent / 100));
      var nowText = (nowCents / 100).toFixed(2);
      var discounted = original.split(wasText).join(nowText);

      slot.innerHTML =
        '<s class="reef-price-was">' +
        original +
        '</s><span class="reef-price-now">' +
        discounted +
        '</span><span class="reef-price-off">' +
        badge +
        '</span><button type="button" class="reef-price-info" data-reef-pb-info aria-label="See all offers">i</button>' +
        (note ? '<span class="reef-price-note">' + note + '</span>' : '');

      el.setAttribute('data-reef-priced', '1');
    });
  }

  function render(root, qty) {
    var config = readConfig(root);
    if (!config) return;

    var state = compute(config, qty);

    var lefts = spaceTicks(root);
    spaceBands(root, lefts);

    var bar = root.querySelector('[data-reef-pb-fill]');
    if (bar) bar.style.width = fillPercent(root, config, qty).toFixed(2) + '%';

    var info = pill(config, qty, state);

    var text = root.querySelector('[data-reef-pb-pill-text]');
    if (text) text.innerHTML = info.text;

    Array.prototype.forEach.call(root.querySelectorAll('[data-reef-pb-icon]'), function (icon) {
      icon.hidden = icon.getAttribute('data-reef-pb-icon') !== info.icon;
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-reef-pb-tick]'), function (tick) {
      var index = parseInt(tick.getAttribute('data-reef-pb-tick'), 10);
      tick.classList.toggle('is-reached', state.reachedIndex >= index);
    });

    Array.prototype.forEach.call(root.querySelectorAll('[data-reef-pb-band]'), function (band) {
      var index = parseInt(band.getAttribute('data-reef-pb-band'), 10);
      band.classList.toggle('is-active', state.reachedIndex >= index);
    });

    root.setAttribute('data-reef-pb-qty', qty);
    root.setAttribute('data-reef-pb-ready', '1');
  }

  function hydrateShells() {
    var source = document.querySelector('.reef-pb--header .reef-pb__inner');
    if (!source) return;

    Array.prototype.forEach.call(document.querySelectorAll('[data-reef-pb-shell]'), function (shell) {
      if (shell.firstElementChild) return;
      shell.innerHTML = source.outerHTML;
      shell.removeAttribute('data-reef-pb-ready');
    });
  }

  function markModalRows(qty) {
    var modal = document.getElementById('ReefPromoModal');
    if (!modal) return;

    var best = -1;
    var bestRow = null;

    Array.prototype.forEach.call(modal.querySelectorAll('[data-reef-modal-row]'), function (row) {
      var threshold = parseInt(row.getAttribute('data-reef-modal-row'), 10);
      row.classList.remove('is-current');
      if (qty >= threshold && threshold > best) {
        best = threshold;
        bestRow = row;
      }
    });

    if (bestRow) bestRow.classList.add('is-current');
  }

  function renderAll(qty) {
    lastQty = qty;
    hydrateShells();
    Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), function (root) {
      render(root, qty);
    });
    markModalRows(qty);
    applyPricing(qty);
  }

  function refresh() {
    fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var qty = eligibleQty(cart);
        auditCart(cart, qty);
        renderAll(qty);
      })
      .catch(function () {});
  }

  function adoptNewBars() {
    hydrateShells();

    var pending = document.querySelectorAll(SELECTOR + ':not([data-reef-pb-ready])');
    if (!pending.length) return;

    Array.prototype.forEach.call(pending, function (root) {
      render(root, lastQty);
    });
  }

  function observeDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;

    new MutationObserver(adoptNewBars).observe(drawer, { childList: true, subtree: true });
  }

  function headerElement() {
    return document.querySelector('.section-header') || document.querySelector('.header-wrapper');
  }

  function measureHeader() {
    var header = headerElement();
    var barSection = document.querySelector('.reef-pb-section-wrapper');

    var headerRaw = header ? header.getBoundingClientRect().height : 0;
    var barRaw = barSection ? barSection.getBoundingClientRect().height : 0;

    // Header heights are often fractional (103.5px). Rounding up leaves a
    // hairline gap under the sticky header that the page shows through, so
    // floor it and tuck the bar 1px behind. The header sits above the bar in
    // the stacking order, so the overlap is never visible.
    var pin = headerRaw > 0 ? Math.max(Math.floor(headerRaw) - 1, 0) : 0;

    if (headerRaw > 0) {
      document.documentElement.style.setProperty('--reef-header-h', pin + 'px');
    }

    document.documentElement.style.setProperty(
      '--reef-sticky-top',
      pin + Math.ceil(barRaw) + 'px'
    );
  }

  function trackHeader() {
    measureHeader();

    window.addEventListener('resize', measureHeader, { passive: true });
    window.addEventListener('load', measureHeader);

    if ('ResizeObserver' in window) {
      var header = headerElement();
      if (header) new ResizeObserver(measureHeader).observe(header);

      var barSection = document.querySelector('.reef-pb-section-wrapper');
      if (barSection) new ResizeObserver(measureHeader).observe(barSection);
    }
  }

  function openModal() {
    var modal = document.getElementById('ReefPromoModal');
    if (!modal) return;
    markModalRows(lastQty);
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    var close = modal.querySelector('[data-reef-modal-close]');
    if (close) close.focus();
  }

  function closeModal() {
    var modal = document.getElementById('ReefPromoModal');
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');

    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  function addGiftBag(button) {
    var id = button.getAttribute('data-variant-id');
    if (!id) return;

    var text = button.textContent;
    button.disabled = true;
    button.textContent = button.getAttribute('data-busy-label') || 'Adding';

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: id, quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        var drawer = document.querySelector('cart-drawer');

        if (drawer && state.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(state);
        } else {
          window.location.reload();
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'reef-gift-bag' });
        }

        refresh();
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = text;
      });
  }

  function onClick(event) {
    if (!event.target.closest) return;

    if (event.target.closest('[data-reef-pb-info]')) {
      event.preventDefault();
      openModal();
      return;
    }

    // The panel's bottom button closes rather than navigating. It is still an
    // <a> in the section markup, so preventDefault has to stop the href - the
    // X at the top and this button now do the same thing, at opposite ends of
    // the panel, which is what a phone thumb wants.
    if (
      event.target.closest('[data-reef-modal-close]') ||
      event.target.closest('[data-reef-modal-overlay]') ||
      event.target.closest('.reef-modal__cta')
    ) {
      event.preventDefault();
      closeModal();
      return;
    }

    var giftButton = event.target.closest('[data-reef-gb-add]');
    if (giftButton && !giftButton.disabled) {
      event.preventDefault();
      addGiftBag(giftButton);
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.keyCode === 27) closeModal();
  }

  function init() {
    injectStyles();
    detachModal();

    var main = document.querySelector('.reef-pb--header');
    var serverQty = main ? parseInt(main.getAttribute('data-reef-pb-qty'), 10) || 0 : 0;

    renderAll(serverQty);

    // With items already in the basket there is something to verify against,
    // so pull the cart on load rather than waiting for the next update. This
    // is what makes the safety check cover someone landing on a product page
    // with a basket left from a previous visit.
    if (serverQty > 0) refresh();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }

    document.addEventListener('reef:promo-bar:refresh', refresh);
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    observeDrawer();
    trackHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
